import React, {FC, FormEvent, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {Link, useParams} from 'react-router-dom'
import {
  createPlatformSettingAsync,
  fetchConnectedPlatformAsync,
  retrivePlatformDataAsync,
} from 'src/redux/actions/platformActions'
import {
  getSettingsOfPrimaryPlatformAsyncNew,
  getSettingsOfSelectedPlatformAsyncNew,
} from 'src/redux/actions/platformSettingActions'
import {useAppDispatch, useAppSelector} from 'src/redux/storeHooks'
import {useApplyPlatformSettingShortcodes} from 'src/utils/usePlatformSettingDisplayText'
import {PlatformDataType, RetrievePlatformDataType} from 'src/interfaces/Platform.interface'
import Loader from 'src/utils/Loader'
import useAppToast from 'src/hooks/useAppToast'
import InfoTooltip from './components/InfoTooltip'
import SettingFileActionCallout from './components/SettingFileActionCallout'
import {getIdentifierSettingFileChildActions} from 'src/utils/settingFileFieldMappings'
import 'src/app/features/advanced-channel-settings/styles/advanced-channel-settings.css'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faCircleInfo} from '@fortawesome/free-solid-svg-icons'
import {
  attachIdentifierPairIdForSave,
  getIdentifierFieldKeysForRolePair as getIdentifierFieldKeys,
  mergeSavedIdentifierRowPersistState,
  resolveIdentifierPairForRow,
  resolveIdentifierRowPersistState,
  findSavedProductIdentifierPair,
  pairMatchesPlatformIds,
  type ResolvedIdentifierPair,
} from 'src/utils/productIdentifierResolveUtils'
import {
  shouldSwapOmsShippingIdentifierLayout,
  type RelocationStoreSnapshot,
} from 'src/utils/channelSettingsOmsShippingRelocation'

type IdentifierOption = {
  id?: number | string
  name: string
  display_name: string
}

type ProductIdentifierPair = {
  id?: number | null
  platform_setting?: number
  platform1?: number
  platform2?: number
  platform_identifier1?: string
  platform_identifier2?: string
  platform_product_identifier1?: string | null
  platform_product_identifier2?: string | null
}

export type ProductIdentifierMappingPayload = {
  productIdentifiers: ProductIdentifierPair[]
  legacyScalars: Record<string, any>
}

const EMPTY_IDENTIFIER_SETTINGS: any[] = []
const EMPTY_IDENTIFIER_SETTINGS_BY_PLATFORM_ID: Record<number, any[]> = {}

type IdentifierRow = {
  relatedPlatformId: number
  relatedAvailablePlatformId: number
  role: string
  displayName: string
  selectedLabel: string
  selectedDescription: string
  selectedPlaceholder: string
  selectedStoreName: string
  relatedLabel: string
  relatedDescription: string
  relatedPlaceholder: string
  relatedStoreName: string
  savedPairId?: number
  savedPlatformSettingId?: number
  selectedIdentifier: string
  relatedIdentifier: string
  selectedIdentifierLocked?: boolean
  relatedIdentifierLocked?: boolean
  selectedOptions: IdentifierOption[]
  relatedOptions: IdentifierOption[]
  isLoadingOptions: boolean
  selectedSettingsData?: any[]
  relatedSettingsData?: any[]
  resolvedFromSecondaryStore?: boolean
  isInverted?: boolean
}

const roleLabels: Record<string, string> = {
  PRIMARY: 'Source',
  SECONDARY: 'Destination',
  ACCOUNTING: 'Accounting',
  SHIPPING: 'Shipping',
  ERP: 'ERP',
  OMS: 'OMS',
  TICKET: 'HELPDESK',
}

const settingTypeByRole: Record<string, string> = {
  PRIMARY: 'MAIN_STORE_SETTING',
  ACCOUNTING: 'ACCOUNTING_STORE_SETTING',
  SHIPPING: 'SHIPPING_STORE_SETTING',
  ERP: 'ERP_STORE_SETTING',
  OMS: 'OMS_STORE_SETTING',
  TICKET: 'TICKET_STORE_SETTING',
  SECONDARY: 'SECONDARY_STORE_SETTING',
}

const normalizeRole = (role?: string | null): string => `${role ?? ''}`.trim().toUpperCase()

const isEditableIdentifierRow = (row: IdentifierRow): boolean => !row.resolvedFromSecondaryStore

const getPersistedIdentifierRowState = (
  pair?: Pick<ProductIdentifierPair, 'id'> | null,
  liveLock?: {
    selectedIdentifier?: string
    relatedIdentifier?: string
    resolvedFromSecondaryStore?: boolean
  }
) => resolveIdentifierRowPersistState(pair, liveLock)

const attachPersistedIdentifierPairId = (
  pair: ProductIdentifierPair,
  candidateId: unknown
): ProductIdentifierPair => attachIdentifierPairIdForSave(pair, candidateId)

const isEligibleIdentifierMappingRow = (row: {
  relatedAvailablePlatformId: number
  role: string
}): boolean => Number.isFinite(row.relatedAvailablePlatformId) && row.role !== 'SECONDARY'

const matchesRelatedRoleFilter = (
  role: string,
  includeRelatedRoles?: string[],
  excludeRelatedRoles?: string[]
): boolean => {
  const normalizedRole = normalizeRole(role)
  if (includeRelatedRoles?.length && !includeRelatedRoles.map(normalizeRole).includes(normalizedRole)) {
    return false
  }
  if (excludeRelatedRoles?.length && excludeRelatedRoles.map(normalizeRole).includes(normalizedRole)) {
    return false
  }
  return true
}

const getIdentifierMappingEmptyMessage = (
  connectedPlatforms: PlatformDataType[],
  selectedPlatformId: number
): string => {
  const hasOtherConnectedPlatforms = connectedPlatforms.some(
    (platform) => Number(platform.id) && Number(platform.id) !== selectedPlatformId
  )

  if (!hasOtherConnectedPlatforms) {
    return 'No connected platforms found for identifier mapping.'
  }

  return 'No eligible platforms found for identifier mapping. Connect a Source, ERP, Shipping, or Accounting platform, or configure mapping from your destination channel settings.'
}

const normalizeIdentifierValue = (value: any): string => {
  if (value === null || value === undefined) return ''
  return `${value}`.trim()
}

const getPlatformNature = (platform?: Partial<PlatformDataType> | null): string =>
  normalizeRole(platform?.platform_info?.nature ?? (platform as {nature?: string})?.nature)

const SECONDARY_NATURE_SUFFIX = '_SECONDARY'

const getStoreFlagKeyFromRole = (role: string): string =>
  `is${role.charAt(0).toUpperCase()}${role.slice(1).toLowerCase()}Store`

const isTruthyStoreFlag = (value: unknown): boolean => Boolean(value)

const getConnectedRoleFromCompositeNature = (
  nature: string,
  platform: Partial<PlatformDataType>
): string | null => {
  const normalizedNature = normalizeRole(nature)

  if (normalizedNature === 'SECONDARY') return 'SECONDARY'
  if (!normalizedNature.endsWith(SECONDARY_NATURE_SUFFIX)) return null

  const role = normalizedNature.slice(0, -SECONDARY_NATURE_SUFFIX.length)
  if (!role) return 'SECONDARY'

  const storeFlagKey = getStoreFlagKeyFromRole(role)
  return isTruthyStoreFlag((platform as Record<string, unknown>)[storeFlagKey]) ? role : 'SECONDARY'
}

const getPlatformRole = (platform?: Partial<PlatformDataType> | null): string => {
  if (!platform) return 'SECONDARY'
  if (platform.isMainStore) return 'PRIMARY'
  if (platform.isAccountingStore) return 'ACCOUNTING'
  if (platform.isShippingStore) return 'SHIPPING'
  if (platform.isErpStore) return 'ERP'
  if (platform.isOmsStore) return 'OMS'
  if ((platform as any).isTicketStore) return 'TICKET'

  const nature = getPlatformNature(platform)
  const type = normalizeRole((platform as any)?.platform_info?.type ?? (platform as any)?.type)
  const code = normalizeRole(platform.code)
  const name = normalizeRole(platform.name)

  if (type === 'TICKET') return 'TICKET'

  const connectedCompositeRole = getConnectedRoleFromCompositeNature(nature, platform)
  if (connectedCompositeRole) return connectedCompositeRole

  if (nature.includes('ACCOUNTING')) return 'ACCOUNTING'
  if (nature.includes('SHIPPING')) return 'SHIPPING'
  if (nature.includes('ERP') || code.includes('ERP') || name.includes('ERP')) return 'ERP'
  if (nature.includes('OMS') || code.includes('OMS') || name.includes('OMS')) return 'OMS'
  if (nature.includes('MAIN') || nature.includes('PRIMARY')) return 'PRIMARY'
  return 'SECONDARY'
}

const assignIdentifierFields = (
  payload: Record<string, any>,
  role: string,
  sourceRole: string,
  value: string
) => {
  const fieldMap = getIdentifierFieldKeys(role, sourceRole)

  fieldMap?.forEach((field) => {
    payload[field] = value
  })
}

export const applyLegacyIdentifierScalars = (
  payload: Record<string, any>,
  productIdentifiers: ProductIdentifierPair[]
) => {
  const appliedRolePairs = new Set<string>()

  productIdentifiers.forEach((pair) => {
    const selectedRole = normalizeRole(pair.platform_identifier1)
    const relatedRole = normalizeRole(pair.platform_identifier2)
    const rolePairKey = [selectedRole, relatedRole].sort().join(':')
    if (appliedRolePairs.has(rolePairKey)) return

    const selectedValue = normalizeIdentifierValue(pair.platform_product_identifier1)
    const relatedValue = normalizeIdentifierValue(pair.platform_product_identifier2)

    assignIdentifierFields(payload, selectedRole, relatedRole, selectedValue)
    assignIdentifierFields(payload, relatedRole, selectedRole, relatedValue)

    appliedRolePairs.add(rolePairKey)
  })
}

export const buildProductIdentifierMappingPayload = (
  rows: IdentifierRow[],
  selectedPlatformId: number,
  selectedRole: string,
  existingPairs: ProductIdentifierPair[] = []
): ProductIdentifierMappingPayload => {
  const editableRows = rows.filter((row) => isEditableIdentifierRow(row))

  const nextProductIdentifiers = editableRows
    .map((row) => {
      const existingPair = findSavedProductIdentifierPair(
        existingPairs,
        selectedPlatformId,
        row.relatedPlatformId,
        selectedRole,
        row.role
      )

      const isRowInverted = Boolean(row.isInverted)

      const nextPair: ProductIdentifierPair = {
        platform1: isRowInverted ? row.relatedPlatformId : selectedPlatformId,
        platform2: isRowInverted ? selectedPlatformId : row.relatedPlatformId,
        platform_identifier1: isRowInverted ? row.role : selectedRole,
        platform_identifier2: isRowInverted ? selectedRole : row.role,
        platform_product_identifier1: normalizeIdentifierValue(row.selectedIdentifier),
        platform_product_identifier2: normalizeIdentifierValue(row.relatedIdentifier),
      }

      const persistedPair = attachPersistedIdentifierPairId(
        nextPair,
        existingPair?.id ?? row.savedPairId
      )
      if (existingPair?.platform_setting || row.savedPlatformSettingId) {
        persistedPair.platform_setting = existingPair?.platform_setting ?? row.savedPlatformSettingId
      }

      return persistedPair
    })
    .filter(
      (pair) =>
        normalizeIdentifierValue(pair.platform_product_identifier1) &&
        normalizeIdentifierValue(pair.platform_product_identifier2)
    )

  const otherPairs = existingPairs.filter(
    (pair) =>
      !editableRows.some((row) =>
        pairMatchesPlatformIds(pair, selectedPlatformId, row.relatedPlatformId)
      )
  )

  const legacyScalars: Record<string, any> = {}
  applyLegacyIdentifierScalars(legacyScalars, nextProductIdentifiers)

  return {
    productIdentifiers: [...otherPairs, ...nextProductIdentifiers],
    legacyScalars,
  }
}

const normalizeOptions = (options: any[] = []): IdentifierOption[] =>
  options
    .map((option) => ({
      id: option?.id ?? option?.name,
      name: normalizeIdentifierValue(option?.name),
      display_name: normalizeIdentifierValue(option?.display_name ?? option?.name),
    }))
    .filter((option) => option.name)

const ensureOption = (options: IdentifierOption[], value: string): IdentifierOption[] => {
  const normalizedValue = normalizeIdentifierValue(value)
  if (!normalizedValue) return options
  if (options.some((option) => option.name === normalizedValue)) return options
  return [{id: normalizedValue, name: normalizedValue, display_name: normalizedValue}, ...options]
}

const hasIdentifierOptions = (setting: any): boolean =>
  Array.isArray(setting?.setting_options) && setting.setting_options.length > 0

const extractIdentifierOptions = (settings: any[] = []): IdentifierOption[] => {
  const identifierSetting =
    settings.find(
      (setting) => setting?.group_code === 'IDENTIFIER' && hasIdentifierOptions(setting)
    ) ?? settings.find((setting) => hasIdentifierOptions(setting))

  return normalizeOptions(identifierSetting?.setting_options ?? [])
}

const extractIdentifierSetting = (settings: any[] = []) =>
  settings.find(
    (setting) => setting?.group_code === 'IDENTIFIER' && hasIdentifierOptions(setting)
  ) ??
  settings.find((setting) => hasIdentifierOptions(setting)) ??
  null

const IdentifierPairFileActionPanel: FC<{
  selectedSettings?: any[]
  selectedPlatformId: number
  selectedRole: string
  relatedSettings?: any[]
  relatedPlatformId: number
  relatedRole: string
  disabled?: boolean
}> = ({
  selectedSettings = [],
  selectedPlatformId,
  selectedRole,
  relatedSettings = [],
  relatedPlatformId,
  relatedRole,
  disabled,
}) => {
  const fileActionSides = useMemo(() => {
    const sides = [
      {
        role: normalizeRole(selectedRole),
        platformId: selectedPlatformId,
        children: getIdentifierSettingFileChildActions(selectedSettings),
      },
      {
        role: normalizeRole(relatedRole),
        platformId: relatedPlatformId,
        children: getIdentifierSettingFileChildActions(relatedSettings),
      },
    ].filter((side) => side.children.length > 0 && Number.isFinite(side.platformId))

    const roleOrder: Record<string, number> = {PRIMARY: 0, SECONDARY: 1}

    return sides.sort(
      (left, right) => (roleOrder[left.role] ?? 99) - (roleOrder[right.role] ?? 99)
    )
  }, [
    relatedPlatformId,
    relatedRole,
    relatedSettings,
    selectedPlatformId,
    selectedRole,
    selectedSettings,
  ])

  if (!fileActionSides.length) return null

  return (
    <div className='identifier-pair-file-actions col-12 px-4 pb-3'>
      {fileActionSides.map((side) => (
        <SettingFileActionCallout
          key={`${side.role}-${side.platformId}`}
          childSettings={side.children}
          platformId={side.platformId}
          disabled={disabled}
        />
      ))}
    </div>
  )
}

const IdentifierSelect: FC<{
  label: string
  description?: string
  placeholder?: string
  storeName?: string
  value: string
  options: IdentifierOption[]
  disabled?: boolean
  onChange: (value: string) => void
}> = ({label, description, placeholder, storeName, value, options, disabled, onChange}) => {
  const formatSettingDisplayText = useApplyPlatformSettingShortcodes()
  const optionLabel = (displayName?: string | null) => formatSettingDisplayText(displayName)
  const mergedOptions = useMemo(() => ensureOption(options, value), [options, value])
  const [searchTerm, setSearchTerm] = useState('')
  const [isUserTyping, setIsUserTyping] = useState(false)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [visibleOptions, setVisibleOptions] = useState<IdentifierOption[]>([])
  const [loadingMore, setLoadingMore] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const limit = 10

  const selectedOption = mergedOptions.find((option) => option.name === value)
  const displayRevision = formatSettingDisplayText(
    '[channel_name][user_channel_name][related_channel_name][related_user_channel_name][source_channel_name]'
  )
  const filteredOptions = useMemo(
    () =>
      searchTerm
        ? mergedOptions.filter((option) =>
            optionLabel(option.display_name).toLowerCase().includes(searchTerm.toLowerCase())
          )
        : mergedOptions,
    // displayRevision refreshes the filter once platform names used by option labels resolve
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mergedOptions, searchTerm, displayRevision]
  )

  useEffect(() => {
    setVisibleOptions(filteredOptions.slice(0, limit))
  }, [filteredOptions])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setDropdownOpen(false)
        setIsUserTyping(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const loadMore = () => {
    setVisibleOptions((prevOptions) => filteredOptions.slice(0, prevOptions.length + limit))
    setLoadingMore(false)
  }

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const {scrollTop, scrollHeight, clientHeight} = event.currentTarget
    const isBottom = scrollHeight - scrollTop <= clientHeight + 1

    if (isBottom && !loadingMore && visibleOptions.length < filteredOptions.length) {
      setLoadingMore(true)
      loadMore()
    }
  }

  const handleSelect = (option: IdentifierOption) => {
    onChange(option.name)
    setSearchTerm('')
    setIsUserTyping(false)
    setDropdownOpen(false)
  }

  return (
    <div ref={containerRef} className='col-12 col-sm-6 mb-3 position-relative'>
      <div className='position-relative w-100'>
        {label ? (
          <label className='form-label required'>
            <span title={storeName || undefined}>{optionLabel(label)}</span>
            &nbsp;&nbsp;
            {description ? <InfoTooltip message={description} /> : null}
          </label>
        ) : null}
        <input
          type='text'
          ref={inputRef}
          className='form-select form-select-lg'
          placeholder={optionLabel(placeholder) || 'Select Unique identifier'}
          value={isUserTyping ? searchTerm : optionLabel(selectedOption?.display_name)}
          disabled={disabled}
          required
          autoComplete='off'
          onChange={(event) => {
            setSearchTerm(event.target.value)
            setIsUserTyping(true)
            setDropdownOpen(true)
          }}
          onFocus={() => setDropdownOpen(true)}
        />

        {dropdownOpen && !disabled ? (
          <div
            className='dropdown-menu show w-100 mt-0 border shadow overflow-auto fs-5'
            style={{maxHeight: '200px'}}
            onScroll={handleScroll}
          >
            {visibleOptions.length === 0 ? (
              <div className='dropdown-item text-muted'>No results</div>
            ) : (
              visibleOptions.map((option) => (
                <button
                  key={`${option.id ?? option.name}_${option.name}`}
                  className='dropdown-item'
                  type='button'
                  onClick={() => handleSelect(option)}
                >
                  {optionLabel(option.display_name)}
                </button>
              ))
            )}
          </div>
        ) : null}
      </div>
    </div>
  )
}

// IDENTIFIER SELECT skips the BOOLEAN parent card, so this row owns the same
// file-action-first order used by other setting groups: callout, then fields.
const IdentifierPairRow: FC<{
  row: IdentifierRow
  selectedName: string
  selectedPlatformId: number
  selectedRole: string
  selectedSettings: any[]
  relatedSettings: any[]
  relocationSnapshot?: RelocationStoreSnapshot
  disabled?: boolean
  onUpdate: (relatedPlatformId: number, patch: Partial<IdentifierRow>) => void
}> = ({
  row,
  selectedName,
  selectedPlatformId,
  selectedRole,
  selectedSettings,
  relatedSettings,
  relocationSnapshot,
  disabled,
  onUpdate,
}) => {
  const isOmsShippingLayoutSwap = shouldSwapOmsShippingIdentifierLayout(
    relocationSnapshot,
    selectedRole,
    row.role
  )
  const selectedSelect = (
    <IdentifierSelect
      label={row.selectedLabel}
      description={row.selectedDescription}
      placeholder={row.selectedPlaceholder}
      storeName={row.selectedStoreName}
      value={row.selectedIdentifier}
      options={row.selectedOptions}
      disabled={disabled || row.selectedIdentifierLocked}
      onChange={(value) => onUpdate(row.relatedPlatformId, {selectedIdentifier: value})}
    />
  )
  const relatedSelect = (
    <IdentifierSelect
      label={row.relatedLabel}
      description={row.relatedDescription}
      placeholder={row.relatedPlaceholder}
      storeName={row.relatedStoreName}
      value={row.relatedIdentifier}
      options={row.relatedOptions}
      disabled={disabled || row.relatedIdentifierLocked}
      onChange={(value) => onUpdate(row.relatedPlatformId, {relatedIdentifier: value})}
    />
  )

  return (
    <div className='row pb-4 mb-2'>
      <IdentifierPairFileActionPanel
        selectedSettings={selectedSettings}
        selectedPlatformId={selectedPlatformId}
        selectedRole={selectedRole}
        relatedSettings={relatedSettings}
        relatedPlatformId={row.relatedPlatformId}
        relatedRole={row.role}
        disabled={disabled}
      />
      {isOmsShippingLayoutSwap ? relatedSelect : selectedSelect}
      {isOmsShippingLayoutSwap ? selectedSelect : relatedSelect}
      <div className='px-4 d-flex fw-medium pb-3' style={{color: 'darkgrey'}}>
        <span style={{margin: '1px 4px', color: 'darkgrey'}}>
          <FontAwesomeIcon icon={faCircleInfo} />
        </span>
        {row.isLoadingOptions
          ? 'Loading identifier options...'
          : `Important Note : Here you need to map the product identifier of ${row.displayName} with the product identifier of ${selectedName}. This mapping is important because it will be used to identify products across your channels for inventory & order management.`}
      </div>
    </div>
  )
}

type HubSavedIdentifierSource = {
  platformId: number
  platformSettings: Record<string, any>
  productIdentifiers: ProductIdentifierPair[]
}

/**
 * Resolves the OMS↔Shipping identifier pair persisted on the OMS (hub) platform for display on the
 * Shipping settings page. Tries product_identifiers first, then legacy scalar fields on OMS
 * platform_settings (oms_identifier / shipping_identifier) — the same fallback OMS settings uses.
 */
const resolveHubStoredIdentifierPair = (
  hubSource: HubSavedIdentifierSource,
  selectedPlatformId: number
): ResolvedIdentifierPair | null => {
  const omsPlatformId = Number(hubSource.platformId)

  const hubPair = hubSource.productIdentifiers.find((pair) => {
    const platformsMatch =
      (Number(pair.platform1) === omsPlatformId &&
        Number(pair.platform2) === selectedPlatformId) ||
      (Number(pair.platform1) === selectedPlatformId && Number(pair.platform2) === omsPlatformId)
    if (!platformsMatch) return false

    const roles = [normalizeRole(pair.platform_identifier1), normalizeRole(pair.platform_identifier2)]
    return roles.includes('OMS') && roles.includes('SHIPPING')
  })

  if (hubPair) {
    const omsIsPlatform1 = Number(hubPair.platform1) === omsPlatformId
    const relatedIdentifier = normalizeIdentifierValue(
      omsIsPlatform1 ? hubPair.platform_product_identifier1 : hubPair.platform_product_identifier2
    )
    const selectedIdentifier = normalizeIdentifierValue(
      omsIsPlatform1 ? hubPair.platform_product_identifier2 : hubPair.platform_product_identifier1
    )

    if (selectedIdentifier && relatedIdentifier) {
      return {
        savedPair: {
          ...hubPair,
          platform1: selectedPlatformId,
          platform2: omsPlatformId,
          platform_identifier1: 'SHIPPING',
          platform_identifier2: 'OMS',
          platform_product_identifier1: selectedIdentifier,
          platform_product_identifier2: relatedIdentifier,
        },
        selectedIdentifier,
        relatedIdentifier,
      }
    }
  }

  // Legacy scalar fallback: OMS DB often stores oms_identifier + shipping_identifier on the hub
  // platform_settings while product_identifiers[] is empty.
  const scalarResolved = resolveIdentifierPairForRow({
    settings: hubSource.platformSettings,
    pairs: hubSource.productIdentifiers,
    selectedPlatformId,
    relatedPlatformId: omsPlatformId,
    selectedRole: 'SHIPPING',
    relatedRole: 'OMS',
    allowRootFallback: true,
  })

  if (!scalarResolved.selectedIdentifier || !scalarResolved.relatedIdentifier) {
    return null
  }

  return {
    savedPair: scalarResolved.savedPair
      ? {
          ...scalarResolved.savedPair,
          platform1: selectedPlatformId,
          platform2: omsPlatformId,
          platform_identifier1: 'SHIPPING',
          platform_identifier2: 'OMS',
          platform_product_identifier1: scalarResolved.selectedIdentifier,
          platform_product_identifier2: scalarResolved.relatedIdentifier,
        }
      : undefined,
    selectedIdentifier: scalarResolved.selectedIdentifier,
    relatedIdentifier: scalarResolved.relatedIdentifier,
  }
}

export const ProductIdentifierMappingSection: FC<{
  availablePlatformId?: string | number
  selectedPlatformId?: string | number
  currentSettingPlatformId?: string | number
  retrievedPlatform: RetrievePlatformDataType | null
  selectedSettings?: any[]
  relatedSettingsByPlatformId?: Record<number, any[]>
  disabled?: boolean
  includeRelatedRoles?: string[]
  excludeRelatedRoles?: string[]
  hubSavedIdentifierSource?: HubSavedIdentifierSource | null
  relocationSnapshot?: RelocationStoreSnapshot
  onRowsChange?: (payload: ProductIdentifierMappingPayload, hasIncompleteRows: boolean) => void
}> = ({
  availablePlatformId,
  selectedPlatformId,
  currentSettingPlatformId,
  retrievedPlatform,
  selectedSettings = EMPTY_IDENTIFIER_SETTINGS,
  relatedSettingsByPlatformId = EMPTY_IDENTIFIER_SETTINGS_BY_PLATFORM_ID,
  disabled,
  includeRelatedRoles,
  excludeRelatedRoles,
  hubSavedIdentifierSource = null,
  relocationSnapshot,
  onRowsChange,
}) => {
  const dispatch = useAppDispatch()
  const {fetchedConnectedPlatformData, fetchedConnectedPlatformLoading} = useAppSelector(
    (state) => state.fetchConnectedPlatform
  )
  const [rows, setRows] = useState<IdentifierRow[]>([])
  const [loading, setLoading] = useState(false)
  const loadRowsGenerationRef = useRef(0)
  const connectedPlatforms = useMemo(
    () => (Array.isArray(fetchedConnectedPlatformData) ? fetchedConnectedPlatformData : []),
    [fetchedConnectedPlatformData]
  )
  const numericSelectedPlatformId = Number(selectedPlatformId)
  const numericAvailablePlatformId = Number(availablePlatformId)
  // Stable primitive keys so new array/object literals from the parent do not retrigger the load effect.
  const includeRelatedRolesKey = (includeRelatedRoles ?? []).map(normalizeRole).sort().join(',')
  const excludeRelatedRolesKey = (excludeRelatedRoles ?? []).map(normalizeRole).sort().join(',')
  const hubSavedIdentifierSourceKey = hubSavedIdentifierSource
    ? `${hubSavedIdentifierSource.platformId}:${
        hubSavedIdentifierSource.productIdentifiers?.length ?? 0
      }:${normalizeIdentifierValue(hubSavedIdentifierSource.platformSettings?.shipping_identifier)}:${
        normalizeIdentifierValue(hubSavedIdentifierSource.platformSettings?.oms_identifier)
      }`
    : ''

  const updateRow = useCallback((relatedPlatformId: number, patch: Partial<IdentifierRow>) => {
    setRows((prevRows) =>
      prevRows.map((row) =>
        row.relatedPlatformId === relatedPlatformId ? {...row, ...patch} : row
      )
    )
  }, [])
  const removeRow = useCallback((relatedPlatformId: number) => {
    setRows((prevRows) => prevRows.filter((row) => row.relatedPlatformId !== relatedPlatformId))
  }, [])

  useEffect(() => {
    if (!retrievedPlatform || !numericSelectedPlatformId || !rows.length) {
      onRowsChange?.({productIdentifiers: [], legacyScalars: {}}, false)
      return
    }

    const selectedRole = getPlatformRole(retrievedPlatform as any)
    const existingPairs: ProductIdentifierPair[] = Array.isArray(
      (retrievedPlatform.platform_settings as any)?.product_identifiers
    )
      ? (retrievedPlatform.platform_settings as any).product_identifiers
      : []
    const payload = buildProductIdentifierMappingPayload(
      rows,
      numericSelectedPlatformId,
      selectedRole,
      existingPairs
    )
    const hasIncompleteRows = rows.some(
      (row) =>
        isEditableIdentifierRow(row) &&
        (!normalizeIdentifierValue(row.selectedIdentifier) ||
          !normalizeIdentifierValue(row.relatedIdentifier))
    )

    onRowsChange?.(payload, hasIncompleteRows)
  }, [numericSelectedPlatformId, onRowsChange, retrievedPlatform, rows])

  useEffect(() => {
    if (
      !retrievedPlatform ||
      !selectedPlatformId ||
      !availablePlatformId ||
      fetchedConnectedPlatformLoading ||
      !connectedPlatforms.length
    ) {
      setRows([])
      return
    }

    let isActive = true
    const loadGeneration = ++loadRowsGenerationRef.current
    const isCurrentLoad = () => isActive && loadGeneration === loadRowsGenerationRef.current

    const loadRows = async () => {
      setLoading(true)
      const selectedRole = getPlatformRole(retrievedPlatform as any)
      const savedPairs: ProductIdentifierPair[] = Array.isArray(
        (retrievedPlatform?.platform_settings as any)?.product_identifiers
      )
        ? (retrievedPlatform.platform_settings as any).product_identifiers
        : []
      const relatedRoleFallbackPlatformIds = connectedPlatforms.reduce<Record<string, number>>(
        (fallbackPlatformIds, platform) => {
          if (!Number(platform.id) || Number(platform.id) === numericSelectedPlatformId) {
            return fallbackPlatformIds
          }

          const role = getPlatformRole(platform)
          if (!fallbackPlatformIds[role]) fallbackPlatformIds[role] = Number(platform.id)
          return fallbackPlatformIds
        },
        {}
      )

      const nextRows = connectedPlatforms
        .filter(
          (platform) => Number(platform.id) && Number(platform.id) !== numericSelectedPlatformId
        )
        .map((platform) => {
          const relatedPlatformId = Number(platform.id)
          const role = getPlatformRole(platform)
          const allowRootFallback = relatedRoleFallbackPlatformIds[role] === relatedPlatformId
          const resolvedPair = (() => {
            const directPair = resolveIdentifierPairForRow({
              settings: (retrievedPlatform?.platform_settings as Record<string, any>) ?? {},
              pairs: savedPairs,
              selectedPlatformId: numericSelectedPlatformId,
              relatedPlatformId,
              selectedRole,
              relatedRole: role,
              allowRootFallback,
            })

            if (directPair.selectedIdentifier && directPair.relatedIdentifier) {
              return {...directPair, resolvedFromSecondaryStore: directPair.resolvedFromSecondaryStore}
            }

            if (
              hubSavedIdentifierSource &&
              normalizeRole(role) === 'OMS' &&
              hubSavedIdentifierSource.platformId === relatedPlatformId
            ) {
              const hubPair = resolveHubStoredIdentifierPair(
                hubSavedIdentifierSource,
                numericSelectedPlatformId
              )
              if (hubPair) {
                return {...hubPair, resolvedFromSecondaryStore: false}
              }
            }

            return directPair
          })()
          const resolvedFromSecondaryStore = resolvedPair.resolvedFromSecondaryStore
          const rowRole = normalizeRole(resolvedPair.savedPair?.platform_identifier2) || role
          const displayName =
            platform.display_name ||
            platform.platform_info?.display_name ||
            platform.name ||
            `${roleLabels[rowRole] ?? rowRole} ${relatedPlatformId}`

          const mappedRow = {
            relatedPlatformId,
            relatedAvailablePlatformId: Number(platform.platform_info?.id),
            role: rowRole,
            displayName,
            selectedLabel: `${
              retrievedPlatform?.platform_info?.display_name ??
              retrievedPlatform?.display_name ??
              'Selected'
            } Store Identifier`,
            selectedDescription: '',
            selectedPlaceholder: 'Select Unique identifier',
            selectedStoreName:
              retrievedPlatform?.display_name ??
              retrievedPlatform?.platform_info?.display_name ??
              '',
            relatedLabel: `${displayName} ${roleLabels[rowRole] ?? rowRole} Identifier`,
            relatedDescription: '',
            relatedPlaceholder: 'Select Unique identifier',
            relatedStoreName: platform.display_name ?? platform.platform_info?.display_name ?? '',
            savedPlatformSettingId: resolvedPair.savedPair?.platform_setting,
            selectedIdentifier: resolvedPair.selectedIdentifier,
            relatedIdentifier: resolvedPair.relatedIdentifier,
            ...getPersistedIdentifierRowState(resolvedPair.savedPair, {
              selectedIdentifier: resolvedPair.selectedIdentifier,
              relatedIdentifier: resolvedPair.relatedIdentifier,
              resolvedFromSecondaryStore,
            }),
            resolvedFromSecondaryStore,
            selectedOptions: ensureOption([], resolvedPair.selectedIdentifier),
            relatedOptions: ensureOption([], resolvedPair.relatedIdentifier),
            isLoadingOptions: true,
          }

          return mappedRow
        })
        .filter((row) => isEligibleIdentifierMappingRow(row))
        .filter((row) =>
          matchesRelatedRoleFilter(row.role, includeRelatedRoles, excludeRelatedRoles)
        )

      if (!isCurrentLoad()) return
      setRows(nextRows)

      const selectedType = settingTypeByRole[selectedRole] ?? 'SECONDARY_STORE_SETTING'
      let selectedSettingsData = selectedSettings
      if (!selectedSettingsData.length && nextRows.length) {
        try {
          const selectedSettingsResponse = await dispatch(
            getSettingsOfSelectedPlatformAsyncNew({
              availablePlatformId: String(numericAvailablePlatformId),
              primaryPlatformId: numericSelectedPlatformId,
              platform_id: String(numericSelectedPlatformId),
              erp_flow: true,
              type: selectedType,
              ...(currentSettingPlatformId != null
                ? {current_setting_platform_id: currentSettingPlatformId}
                : {}),
            })
          )
          selectedSettingsData = selectedSettingsResponse?.payload?.data ?? []
        } catch (error) {
          selectedSettingsData = []
        }
      }
      const selectedIdentifierOptions = extractIdentifierOptions(selectedSettingsData)
      if (!selectedIdentifierOptions.length) {
        if (isCurrentLoad()) {
          setRows([])
          setLoading(false)
        }
        return
      }

      await Promise.all(
        nextRows.map(async (row) => {
          const relatedType = settingTypeByRole[row.role]
          if (!relatedType) {
            if (isCurrentLoad()) {
              updateRow(row.relatedPlatformId, {isLoadingOptions: false})
            }
            return
          }

          try {
            let relatedSettingsData = relatedSettingsByPlatformId[row.relatedPlatformId] ?? []
            if (!relatedSettingsData.length) {
              const relatedSettingsResponse = await dispatch(
                getSettingsOfPrimaryPlatformAsyncNew({
                  primaryAvailablePlatformId: row.relatedAvailablePlatformId,
                  selectedPlatformId: String(numericSelectedPlatformId),
                  primaryPlatformId: row.relatedPlatformId,
                  erp_flow: true,
                  type: relatedType,
                  ...(currentSettingPlatformId != null
                    ? {current_setting_platform_id: currentSettingPlatformId}
                    : {}),
                })
              )
              relatedSettingsData = relatedSettingsResponse?.payload?.data ?? []
            }

            if (!isCurrentLoad()) return

            const selectedSetting = extractIdentifierSetting(selectedSettingsData)
            const relatedSetting = extractIdentifierSetting(relatedSettingsData)
            const relatedIdentifierOptions = extractIdentifierOptions(relatedSettingsData)
            if (!relatedIdentifierOptions.length) {
              if (isCurrentLoad()) {
                removeRow(row.relatedPlatformId)
              }
              return
            }

            updateRow(row.relatedPlatformId, {
              selectedOptions: ensureOption(selectedIdentifierOptions, row.selectedIdentifier),
              relatedOptions: ensureOption(relatedIdentifierOptions, row.relatedIdentifier),
              selectedSettingsData,
              relatedSettingsData,
              selectedDescription: selectedSetting?.description ?? '',
              selectedPlaceholder: selectedSetting?.placeholder ?? 'Select Unique identifier',
              selectedStoreName:
                selectedSetting?.store_name ??
                retrievedPlatform?.display_name ??
                retrievedPlatform?.platform_info?.display_name ??
                '',
              // For OMS relocation rows the relatedLabel is shown on the LEFT (after the UI swap).
              // Use the store-name pattern ("Commercium OMS Store Identifier") so it matches what
              // the OMS settings page shows, instead of the generic API display_name label.
              relatedLabel:
                normalizeRole(row.role) === 'OMS'
                  ? `${row.displayName} Store Identifier`
                  : relatedSetting?.display_name ??
                    `${row.displayName} ${roleLabels[row.role] ?? row.role} Identifier`,
              relatedDescription: relatedSetting?.description ?? '',
              relatedPlaceholder: relatedSetting?.placeholder ?? 'Select Unique identifier',
              relatedStoreName: relatedSetting?.store_name ?? row.displayName,
              isLoadingOptions: false,
            })
          } catch (error) {
            if (isCurrentLoad()) {
              updateRow(row.relatedPlatformId, {isLoadingOptions: false})
            }
          }
        })
      )

      if (isCurrentLoad()) setLoading(false)
    }

    loadRows()

    return () => {
      isActive = false
    }
  }, [
    availablePlatformId,
    connectedPlatforms,
    dispatch,
    excludeRelatedRolesKey,
    fetchedConnectedPlatformLoading,
    hubSavedIdentifierSourceKey,
    includeRelatedRolesKey,
    numericAvailablePlatformId,
    numericSelectedPlatformId,
    retrievedPlatform,
    relatedSettingsByPlatformId,
    selectedSettings,
    selectedPlatformId,
    removeRow,
    updateRow,
  ])

  const selectedName =
    retrievedPlatform?.platform_info?.display_name ??
    retrievedPlatform?.display_name ??
    'Selected Channel'

  if (loading || fetchedConnectedPlatformLoading) {
    return <div className='row p-4 p-sm-4 mx-auto text-muted'>Loading identifier mappings...</div>
  }

  if (!rows.length) {
    return (
      <div className='row p-4 p-sm-4 mx-auto text-muted'>
        {getIdentifierMappingEmptyMessage(connectedPlatforms, numericSelectedPlatformId)}
      </div>
    )
  }

  return (
    <div className='row p-4 p-sm-4 mx-auto'>
      {rows.map((row) => (
        <IdentifierPairRow
          key={row.relatedPlatformId}
          row={row}
          selectedName={selectedName}
          selectedPlatformId={numericSelectedPlatformId}
          selectedRole={getPlatformRole(retrievedPlatform as any)}
          selectedSettings={row.selectedSettingsData ?? selectedSettings}
          relatedSettings={
            row.relatedSettingsData ?? relatedSettingsByPlatformId[row.relatedPlatformId] ?? []
          }
          relocationSnapshot={relocationSnapshot}
          disabled={disabled}
          onUpdate={updateRow}
        />
      ))}
    </div>
  )
}

const ProductIdentifierSettings: FC = () => {
  const {availablePlatformId, selectedPlatformId} = useParams()
  const dispatch = useAppDispatch()
  const {successToast, warningToast, errorToast} = useAppToast()
  const errorToastRef = useRef(errorToast)
  const {fetchedConnectedPlatformData, fetchedConnectedPlatformLoading} = useAppSelector(
    (state) => state.fetchConnectedPlatform
  )

  const [retrievedPlatform, setRetrievedPlatform] = useState<RetrievePlatformDataType | null>(null)
  const [rows, setRows] = useState<IdentifierRow[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)
  const requestedConnectedPlatformsRef = useRef(false)

  useEffect(() => {
    errorToastRef.current = errorToast
  }, [errorToast])

  const numericSelectedPlatformId = Number(selectedPlatformId)
  const numericAvailablePlatformId = Number(availablePlatformId)

  const connectedPlatforms = useMemo(
    () => (Array.isArray(fetchedConnectedPlatformData) ? fetchedConnectedPlatformData : []),
    [fetchedConnectedPlatformData]
  )

  const relocationSnapshot = useMemo(
    () => ({
      mainStoreData: connectedPlatforms.find((p) => p.isMainStore) || null,
      omsStoreData: connectedPlatforms.find((p) => p.isOmsStore) || null,
      shippingStoreData: connectedPlatforms.find((p) => p.isShippingStore) || null,
    }),
    [connectedPlatforms]
  )

  const updateRow = useCallback((relatedPlatformId: number, patch: Partial<IdentifierRow>) => {
    setRows((prevRows) =>
      prevRows.map((row) =>
        row.relatedPlatformId === relatedPlatformId ? {...row, ...patch} : row
      )
    )
  }, [])
  const removeRow = useCallback((relatedPlatformId: number) => {
    setRows((prevRows) => prevRows.filter((row) => row.relatedPlatformId !== relatedPlatformId))
  }, [])

  useEffect(() => {
    if (
      !connectedPlatforms.length &&
      !fetchedConnectedPlatformLoading &&
      !requestedConnectedPlatformsRef.current
    ) {
      requestedConnectedPlatformsRef.current = true
      dispatch(fetchConnectedPlatformAsync())
    }
  }, [connectedPlatforms.length, dispatch, fetchedConnectedPlatformLoading])

  useEffect(() => {
    if (!selectedPlatformId || !availablePlatformId || fetchedConnectedPlatformLoading) return
    if (!connectedPlatforms.length) {
      setRows([])
      setLoading(false)
      return
    }

    let isActive = true

    const loadIdentifierRows = async () => {
      setLoading(true)
      try {
        const retrieveResponse = await dispatch(
          retrivePlatformDataAsync({id: selectedPlatformId, fetch_limited_data: true})
        )
        const retrieved = retrieveResponse.payload as RetrievePlatformDataType

        if (!isActive) return

        setRetrievedPlatform(retrieved)

        const selectedRole = getPlatformRole(retrieved as any)
        const savedPairs: ProductIdentifierPair[] = Array.isArray(
          (retrieved?.platform_settings as any)?.product_identifiers
        )
          ? (retrieved.platform_settings as any).product_identifiers
          : []
        const relatedRoleFallbackPlatformIds = connectedPlatforms.reduce<Record<string, number>>(
          (fallbackPlatformIds, platform) => {
            if (!Number(platform.id) || Number(platform.id) === numericSelectedPlatformId) {
              return fallbackPlatformIds
            }

            const role = getPlatformRole(platform)
            if (!fallbackPlatformIds[role]) {
              fallbackPlatformIds[role] = Number(platform.id)
            }

            return fallbackPlatformIds
          },
          {}
        )

        const nextRows = connectedPlatforms
          .filter(
            (platform) => Number(platform.id) && Number(platform.id) !== numericSelectedPlatformId
          )
          .map((platform) => {
            const relatedPlatformId = Number(platform.id)
            const role = getPlatformRole(platform)
            const allowRootFallback = relatedRoleFallbackPlatformIds[role] === relatedPlatformId
            const resolvedPair = resolveIdentifierPairForRow({
              settings: (retrieved?.platform_settings as Record<string, any>) ?? {},
              pairs: savedPairs,
              selectedPlatformId: numericSelectedPlatformId,
              relatedPlatformId,
              selectedRole,
              relatedRole: role,
              allowRootFallback,
            })
            const resolvedFromSecondaryStore = resolvedPair.resolvedFromSecondaryStore
            const rowRole = normalizeRole(resolvedPair.savedPair?.platform_identifier2) || role
            const displayName =
              platform.display_name ||
              platform.platform_info?.display_name ||
              platform.name ||
              `${roleLabels[rowRole] ?? rowRole} ${relatedPlatformId}`

            const mappedRow = {
              relatedPlatformId,
              relatedAvailablePlatformId: Number(platform.platform_info?.id),
              role: rowRole,
              displayName,
              selectedLabel: `${
                retrieved?.platform_info?.display_name ?? retrieved?.display_name ?? 'Selected'
              } Store Identifier`,
              selectedDescription: '',
              selectedPlaceholder: 'Select Unique identifier',
              selectedStoreName:
                retrieved?.display_name ?? retrieved?.platform_info?.display_name ?? '',
              relatedLabel: `${displayName} ${roleLabels[rowRole] ?? rowRole} Identifier`,
              relatedDescription: '',
              relatedPlaceholder: 'Select Unique identifier',
              relatedStoreName: platform.display_name ?? platform.platform_info?.display_name ?? '',
              savedPlatformSettingId: resolvedPair.savedPair?.platform_setting,
              selectedIdentifier: resolvedPair.selectedIdentifier,
              relatedIdentifier: resolvedPair.relatedIdentifier,
              ...getPersistedIdentifierRowState(resolvedPair.savedPair, {
                selectedIdentifier: resolvedPair.selectedIdentifier,
                relatedIdentifier: resolvedPair.relatedIdentifier,
                resolvedFromSecondaryStore,
              }),
              resolvedFromSecondaryStore,
              selectedOptions: ensureOption([], resolvedPair.selectedIdentifier),
              relatedOptions: ensureOption([], resolvedPair.relatedIdentifier),
              isLoadingOptions: true,
            }

            return mappedRow
          })
          .filter((row) => isEligibleIdentifierMappingRow(row))

        setRows(nextRows)

        await Promise.all(
          nextRows.map(async (row) => {
            const relatedType = settingTypeByRole[row.role]
            if (!relatedType) {
              updateRow(row.relatedPlatformId, {isLoadingOptions: false})
              return
            }

            try {
              const selectedType = settingTypeByRole[selectedRole] ?? 'SECONDARY_STORE_SETTING'
              const [selectedSettingsResponse, relatedSettingsResponse] = await Promise.all([
                dispatch(
                  getSettingsOfSelectedPlatformAsyncNew({
                    availablePlatformId: String(numericAvailablePlatformId),
                    primaryPlatformId: numericSelectedPlatformId,
                    platform_id: String(numericSelectedPlatformId),
                    erp_flow: true,
                    type: selectedType,
                  })
                ),
                dispatch(
                  getSettingsOfPrimaryPlatformAsyncNew({
                    primaryAvailablePlatformId: row.relatedAvailablePlatformId,
                    selectedPlatformId: String(numericSelectedPlatformId),
                    primaryPlatformId: row.relatedPlatformId,
                    erp_flow: true,
                    type: relatedType,
                  })
                ),
              ])

              const selectedIdentifierOptions = extractIdentifierOptions(
                selectedSettingsResponse?.payload?.data ?? []
              )
              const relatedIdentifierOptions = extractIdentifierOptions(
                relatedSettingsResponse?.payload?.data ?? []
              )
              if (!selectedIdentifierOptions.length || !relatedIdentifierOptions.length) {
                removeRow(row.relatedPlatformId)
                return
              }

              const selectedOptions = ensureOption(
                selectedIdentifierOptions,
                row.selectedIdentifier
              )
              const relatedOptions = ensureOption(relatedIdentifierOptions, row.relatedIdentifier)
              const selectedSetting = extractIdentifierSetting(
                selectedSettingsResponse?.payload?.data ?? []
              )
              const relatedSetting = extractIdentifierSetting(
                relatedSettingsResponse?.payload?.data ?? []
              )

              updateRow(row.relatedPlatformId, {
                selectedOptions,
                relatedOptions,
                selectedSettingsData: selectedSettingsResponse?.payload?.data ?? [],
                relatedSettingsData: relatedSettingsResponse?.payload?.data ?? [],
                selectedLabel: `${
                  retrieved?.platform_info?.display_name ?? retrieved?.display_name ?? 'Selected'
                } Store Identifier`,
                selectedDescription: selectedSetting?.description ?? '',
                selectedPlaceholder: selectedSetting?.placeholder ?? 'Select Unique identifier',
                selectedStoreName:
                  selectedSetting?.store_name ??
                  retrieved?.display_name ??
                  retrieved?.platform_info?.display_name ??
                  '',
                relatedLabel:
                  relatedSetting?.display_name ??
                  `${row.displayName} ${roleLabels[row.role] ?? row.role} Identifier`,
                relatedDescription: relatedSetting?.description ?? '',
                relatedPlaceholder: relatedSetting?.placeholder ?? 'Select Unique identifier',
                relatedStoreName: relatedSetting?.store_name ?? row.displayName,
                isLoadingOptions: false,
              })
            } catch (error) {
              updateRow(row.relatedPlatformId, {isLoadingOptions: false})
            }
          })
        )
      } catch (error) {
        errorToastRef.current('Failed to load product identifier settings.')
      } finally {
        if (isActive) setLoading(false)
      }
    }

    loadIdentifierRows()

    return () => {
      isActive = false
    }
  }, [
    availablePlatformId,
    connectedPlatforms,
    dispatch,
    fetchedConnectedPlatformLoading,
    numericAvailablePlatformId,
    numericSelectedPlatformId,
    refreshKey,
    selectedPlatformId,
    removeRow,
    updateRow,
  ])

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    if (!retrievedPlatform) return

    const incompleteRows = rows.filter(
      (row) =>
        isEditableIdentifierRow(row) &&
        (!normalizeIdentifierValue(row.selectedIdentifier) ||
          !normalizeIdentifierValue(row.relatedIdentifier))
    )

    if (incompleteRows.length) {
      warningToast('Please select identifiers for all connected platforms before saving.')
      return
    }

    const selectedRole = getPlatformRole(retrievedPlatform as any)
    const existingPairs: ProductIdentifierPair[] = Array.isArray(
      (retrievedPlatform.platform_settings as any)?.product_identifiers
    )
      ? (retrievedPlatform.platform_settings as any).product_identifiers
      : []

    const editableRows = rows.filter((row) => isEditableIdentifierRow(row))

    const nextProductIdentifiers = editableRows.map((row) => {
      const existingPair = findSavedProductIdentifierPair(
        existingPairs,
        numericSelectedPlatformId,
        row.relatedPlatformId,
        selectedRole,
        row.role
      )

      const isRowInverted = Boolean(row.isInverted)

      const nextPair: ProductIdentifierPair = {
        platform1: isRowInverted ? row.relatedPlatformId : numericSelectedPlatformId,
        platform2: isRowInverted ? numericSelectedPlatformId : row.relatedPlatformId,
        platform_identifier1: isRowInverted ? row.role : selectedRole,
        platform_identifier2: isRowInverted ? selectedRole : row.role,
        platform_product_identifier1: normalizeIdentifierValue(row.selectedIdentifier),
        platform_product_identifier2: normalizeIdentifierValue(row.relatedIdentifier),
      }

      const persistedPair = attachPersistedIdentifierPairId(
        nextPair,
        existingPair?.id ?? row.savedPairId
      )
      if (existingPair?.platform_setting || row.savedPlatformSettingId) {
        persistedPair.platform_setting = existingPair?.platform_setting ?? row.savedPlatformSettingId
      }

      return persistedPair
    })

    const otherPairs = existingPairs.filter(
      (pair) =>
        !editableRows.some((row) =>
          pairMatchesPlatformIds(pair, numericSelectedPlatformId, row.relatedPlatformId)
        )
    )

    const mergedSettings = {
      ...((retrievedPlatform.platform_settings as Record<string, any>) ?? {}),
      product_identifiers: [...otherPairs, ...nextProductIdentifiers],
    }
    applyLegacyIdentifierScalars(mergedSettings, nextProductIdentifiers)

    try {
      setSaving(true)
      const savePayload = {
        platformId: numericSelectedPlatformId,
        ...mergedSettings,
      }

      const response = await dispatch(createPlatformSettingAsync(savePayload as any))

      if (response?.payload?.status_code === 200) {
        successToast('Product identifier settings saved successfully !')
        const retrieveResponse = await dispatch(
          retrivePlatformDataAsync({id: selectedPlatformId, fetch_limited_data: true})
        )
        const refreshed = retrieveResponse.payload as RetrievePlatformDataType
        setRetrievedPlatform(refreshed)
        setRefreshKey((key) => key + 1)
        setRows((prevRows) =>
          prevRows.map((row) => {
            const savedPair = findSavedProductIdentifierPair(
              Array.isArray((refreshed?.platform_settings as any)?.product_identifiers)
                ? (refreshed.platform_settings as any).product_identifiers
                : [],
              numericSelectedPlatformId,
              row.relatedPlatformId,
              selectedRole,
              row.role
            )
            const persistedRowState = mergeSavedIdentifierRowPersistState(row, savedPair)
            return {
              ...row,
              ...persistedRowState,
              savedPlatformSettingId: savedPair?.platform_setting ?? row.savedPlatformSettingId,
            }
          })
        )
        dispatch(fetchConnectedPlatformAsync())
      } else {
        warningToast(
          response?.payload?.data?.message ??
            response?.payload?.data?.massage ??
            'Product identifier settings not updated.'
        )
      }
    } catch (error) {
      errorToast('Failed to save product identifier settings.')
    } finally {
      setSaving(false)
    }
  }

  if (loading || fetchedConnectedPlatformLoading) {
    return <Loader />
  }

  return (
    <>
      <div className='d-flex justify-content-between'>
        <ul className='breadcrumb text-muted fw-semibold fs-6 my-0 mb-2'>
          <li className='breadcrumb-item'>
            <Link to='/listing-channels' className='text-primary text-decoration-none'>
              Manage Channels
            </Link>
          </li>
          <li className='breadcrumb-item'>{retrievedPlatform?.platform_info?.display_name}</li>
        </ul>
      </div>

      <div className={saving ? 'background-disable' : ''}>
        <div className='card'>
          <form onSubmit={handleSubmit}>
            <div className='card-header bg-white'>
              <div className='card-title'>Channel Settings</div>
              <div className='card-toolbar'>
                <button
                  type='button'
                  disabled={saving}
                  onClick={() => setRefreshKey((key) => key + 1)}
                  className='btn btn-sm btn-success ms-2 fw-bold'
                  style={{minWidth: '100px', width: '130px'}}
                >
                  {loading ? 'Refreshing...' : 'Refresh Mapping'}
                </button>
                <button
                  type='submit'
                  disabled={saving || !rows.length}
                  className='btn btn-sm btn-success ms-2 fw-bold w-100px'
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>

            <div className='card-body p-2 px-sm-4'>
              <section>
                <div className='mt-5 border shadow rounded'>
                  <div className='ribbon ribbon-start'>
                    <div className='ribbon-label bg-primary text-uppercase'>Channel Connection</div>
                  </div>
                  <div className='row p-2 p-sm-4 mx-auto'>
                    <div className='row my-4 my-sm-2'>
                      <div className='col-6'>
                        <p className='h4'>
                          {retrievedPlatform?.platform_info?.display_name} Channel Name:
                        </p>
                      </div>
                      <div className='col-6 d-flex align-items-center'>
                        <p className='h4'>{retrievedPlatform?.display_name}</p>
                      </div>
                    </div>
                    <div className='row'>
                      <div className='col-6 h4'>Status:</div>
                      <div className='col-6'>
                        <span
                          className={`badge ${
                            (retrievedPlatform as any)?.isConnected
                              ? 'badge-success'
                              : 'badge-danger'
                          }`}
                          style={{fontSize: '1.3rem', borderRadius: '6px'}}
                        >
                          {(retrievedPlatform as any)?.isConnected ? 'Connected' : 'Not Connected'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <section>
                <div className='mt-5 border shadow rounded'>
                  <div className='ribbon ribbon-start'>
                    <div className='ribbon-label bg-primary'>MAP PRODUCT IDENTIFIER</div>
                  </div>

                  {rows.length ? (
                    <div className='row p-4 p-sm-4 mx-auto'>
                      {rows.map((row) => (
                        <IdentifierPairRow
                          key={row.relatedPlatformId}
                          row={row}
                          selectedName={
                            retrievedPlatform?.platform_info?.display_name ??
                            retrievedPlatform?.display_name ??
                            'Selected Channel'
                          }
                          selectedPlatformId={Number(selectedPlatformId)}
                          selectedRole={getPlatformRole(retrievedPlatform as any)}
                          selectedSettings={row.selectedSettingsData ?? []}
                          relatedSettings={row.relatedSettingsData ?? []}
                          relocationSnapshot={relocationSnapshot}
                          disabled={saving}
                          onUpdate={updateRow}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className='row p-4 p-sm-4 mx-auto text-muted'>
                      {getIdentifierMappingEmptyMessage(
                        connectedPlatforms,
                        numericSelectedPlatformId
                      )}
                    </div>
                  )}
                </div>
              </section>
            </div>
          </form>
        </div>
      </div>
    </>
  )
}

export default ProductIdentifierSettings
